import { useCallback, useState } from 'react';
import { Id, toast } from 'react-toastify';
import { QueryBuilderMode, QuerySource } from '../../types/QueryBuilder';
import { useQueryBuilderCache } from '../../context/QueryBuilderCacheContext';
import { useQueryBuilderData } from '../../context/QueryBuilderDataContext';
import { useQueryBuilder } from './useQueryBuilder';
import { useSimpleQueryBuilder } from './useSimpleQueryBuilder';
import { QUERY_NAME_TAKEN, QUERY_USED_IN_CAMPAIGN, useSaveQuery } from './useSaveQuery';
import { useSaveAndCountQuery } from './useSaveAndCountQuery';
import { useLinkQueryToGroup } from './useLinkQueryToGroup';

const NEW_GROUP_ID = -1;

interface SelectedGroup {
    id: number | null;
    name: string;
}

interface CountDialogState {
    open: boolean;
    queryId: number | null;
    description: string;
}

const NO_GROUP: SelectedGroup = { id: null, name: '' };

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

// La requête comptée a été reprise dans une campagne entre-temps : elle n'est plus mise à jour, un nouveau nom en crée une autre
const USED_IN_CAMPAIGN_MESSAGE = 'Cette requête est désormais utilisée dans une campagne : changez son nom pour en enregistrer une nouvelle';

// Sans toastId (comptage : la progression est affichée dans le dialog), l'erreur ouvre un nouveau toast
const showError = (toastId: Id | null, render: string) => {
    if (toastId === null) {
        toast.error(render, { autoClose: 5000 });
        return;
    }
    toast.update(toastId, { render, type: 'error', isLoading: false, autoClose: 5000 });
};

export const useQueryForm = () => {
    const advanced = useQueryBuilder();
    const simple = useSimpleQueryBuilder();
    const { refetchGroups } = useQueryBuilderData();
    const cache = useQueryBuilderCache();
    const { saveQuery } = useSaveQuery();
    const { saveAndCountQuery, loading: counting } = useSaveAndCountQuery();
    const { linkQueryToGroup } = useLinkQueryToGroup();

    const [mode, setMode] = useState<QueryBuilderMode>('simple');
    const [description, setDescriptionState] = useState('');
    // Nom refusé par l'API car déjà porté par une autre requête : affiché sous le champ jusqu'à la prochaine saisie
    const [nameTaken, setNameTaken] = useState(false);
    // Requête créée par le dernier comptage : recompter ou sauvegarder sous ce nom la met à jour au lieu d'en créer une
    // autre, qui serait refusée comme doublon
    const [counted, setCounted] = useState<{ queryId: number; name: string } | null>(null);
    const [group, setGroup] = useState<SelectedGroup>(NO_GROUP);
    const [saving, setSaving] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [countDialog, setCountDialog] = useState<CountDialogState>({ open: false, queryId: null, description: '' });

    const { resetGroups } = advanced;
    const { resetFields } = simple;
    const activeGroups = mode === 'simple' ? simple.groups : advanced.groups;
    const isValid = mode === 'simple' ? simple.isValid : advanced.isValid;
    const source: QuerySource = mode === 'simple' ? 'SIMPLE' : 'ADVANCED';
    const groupId = group.id && group.id > 0 ? group.id : undefined;
    const groupName = group.name.trim();

    const setDescription = useCallback((value: string) => {
        setDescriptionState(value);
        setNameTaken(false);
    }, []);
    const countedQueryId = (name: string) => (counted && sameName(counted.name, name) ? counted.queryId : undefined);

    const selectGroup = useCallback((id: number | null, name?: string) => setGroup({ id, name: name || '' }), []);
    const createGroup = useCallback((name: string) => setGroup({ id: NEW_GROUP_ID, name }), []);

    const reset = useCallback(() => {
        resetGroups();
        resetFields();
        setDescription('');
        setGroup(NO_GROUP);
        setCounted(null);
    }, [resetGroups, resetFields, setDescription]);

    const closeCountDialog = useCallback(() => setCountDialog(prev => ({ ...prev, open: false })), []);

    const completeQuery = async (toastId: Id | null, queryId: number | undefined, queryName: string): Promise<boolean> => {
        if (groupName) {
            if (!queryId) {
                showError(toastId, 'Requête créée, mais impossible de la lier au groupe');
                return false;
            }
            if (toastId !== null) toast.update(toastId, { render: 'Création du groupe en cours...', isLoading: true });
            if (!(await linkQueryToGroup({ queryId, groupQueryName: groupName, queryName }))) {
                showError(toastId, 'Erreur lors de la création du groupe');
                return false;
            }
            await refetchGroups();
        }
        cache?.invalidate('saved_queries|');
        setRefreshKey(key => key + 1);
        return true;
    };

    const execute = async () => {
        const queryName = description.trim();
        if (!isValid) {
            toast.warning('Veuillez compléter toutes les règles');
            return;
        }
        if (!queryName) {
            toast.warning('Veuillez renseigner un nom de requête avant de compter');
            return;
        }

        const fail = () => {
            closeCountDialog();
            showError(null, 'Erreur lors du comptage de la requête');
        };

        setCountDialog({ open: true, queryId: null, description: queryName });
        try {
            const result = await saveAndCountQuery({
                queryId: countedQueryId(queryName), description: queryName, groups: activeGroups, groupId, source, uniqueName: true,
            });
            if (result === QUERY_NAME_TAKEN) {
                closeCountDialog();
                setNameTaken(true);
                return;
            }
            if (result === QUERY_USED_IN_CAMPAIGN) {
                closeCountDialog();
                setCounted(null);
                showError(null, USED_IN_CAMPAIGN_MESSAGE);
                return;
            }
            if (!result) {
                fail();
                return;
            }
            if (result.queryId) {
                setCounted({ queryId: result.queryId, name: queryName });
                setCountDialog({ open: true, queryId: result.queryId, description: queryName });
            } else {
                closeCountDialog();
            }
            await completeQuery(null, result.queryId, queryName);
        } catch {
            fail();
        }
    };

    const save = async () => {
        const queryName = description.trim();
        if (!isValid || !queryName) {
            toast.warning('Nom requis');
            return;
        }

        const toastId = toast.loading(groupName ? 'Sauvegarde et création du groupe en cours...' : 'Sauvegarde de la requête en cours...');
        setSaving(true);
        try {
            const result = await saveQuery({
                queryId: countedQueryId(queryName), description: queryName, groups: activeGroups, groupId, source, uniqueName: true,
            });
            if (result === QUERY_NAME_TAKEN) {
                showError(toastId, 'Une requête porte déjà ce nom');
                setNameTaken(true);
                return;
            }
            if (result === QUERY_USED_IN_CAMPAIGN) {
                setCounted(null);
                showError(toastId, USED_IN_CAMPAIGN_MESSAGE);
                return;
            }
            if (!result) {
                showError(toastId, 'Erreur lors de la sauvegarde de la requête');
                return;
            }
            if (await completeQuery(toastId, result.queryId, queryName)) {
                toast.update(toastId, { render: `Requête « ${queryName} » sauvegardée`, type: 'success', isLoading: false, autoClose: 3000 });
                reset();
            }
        } catch {
            showError(toastId, 'Erreur lors de la sauvegarde de la requête');
        } finally {
            setSaving(false);
        }
    };

    return {
        mode,
        setMode,
        description,
        setDescription,
        nameTaken,
        selectedGroupId: group.id,
        newGroupName: group.id === NEW_GROUP_ID ? group.name : '',
        selectGroup,
        createGroup,
        advanced,
        simple,
        isValid,
        counting,
        saving,
        reset,
        execute,
        save,
        refreshKey,
        countDialog,
        closeCountDialog,
    };
};

export type QueryForm = ReturnType<typeof useQueryForm>;
